import type { Lang } from '../types';

/**
 * The invited apartment owner's one page. Written for someone who has never heard of Guestbook and
 * is about to be asked for their bank details — so it says who invited them, what connecting
 * actually does, and what the manager will and won't be able to see. That last part is the Art. 13
 * transparency notice as much as it is reassurance.
 */
export interface PublicStripeInviteText {
  loading: string;
  invalidTitle: string;
  invalidBody: string;
  invitedBy: string;
  forWhat: string;
  noAccountNeeded: string;
  termOwnAccount: string;
  termFees: string;
  termVisibility: string;
  countryLabel: string;
  countryHint: string;
  connectCta: string;
  redirecting: string;
  startError: string;
  unfinishedTitle: string;
  unfinishedBody: string;
  continueCta: string;
  connectedTitle: string;
  connectedBody: string;
  connectedDashboard: string;
}

export const publicStripeInviteText: Record<Lang, PublicStripeInviteText> = {
  cs: {
    loading: 'Načítám…',
    invalidTitle: 'Tato pozvánka už neplatí',
    invalidBody: 'Požádejte prosím správce o zaslání nové. Odkazy platí 7 dní a přestanou platit také po dokončení propojení.',
    invitedBy: '{manager} vás zve k propojení vašeho Stripe účtu',
    forWhat: 'Pro platby od hostů: {what}',
    noAccountNeeded: 'Účet v Guestbooku nepotřebujete — tohle je jediná stránka, kterou uvidíte.',
    termOwnAccount: 'Platby od hostů půjdou přímo na váš bankovní účet.',
    termFees: 'Stripe vám bude účtovat vlastní poplatky za zpracování plateb. Guestbook si nebere žádný podíl.',
    termVisibility: '{manager} uvidí pouze to, zda je účet propojený a funkční — ne vaše doklady ani zůstatek.',
    countryLabel: 'Kde máte bankovní účet?',
    countryHint: 'Zemi už nepůjde později změnit, proto ji prosím zkontrolujte.',
    connectCta: 'Propojit se Stripe →',
    redirecting: 'Přesměrování na Stripe…',
    startError: 'Propojení se teď nepodařilo spustit. Zkuste to prosím za chvíli.',
    unfinishedTitle: 'Nastavení ještě není dokončené',
    unfinishedBody: 'Vypadá to, že jste ve Stripe nedokončili všechny kroky. Můžete pokračovat tam, kde jste skončili.',
    continueCta: 'Pokračovat ve Stripe →',
    connectedTitle: 'Hotovo',
    connectedBody: 'Váš Stripe účet je propojený. Platby od hostů budou chodit na váš bankovní účet.',
    connectedDashboard: 'Tuto stránku můžete zavřít — vše ostatní spravujete ve svém Stripe účtu.',
  },
  en: {
    loading: 'Loading…',
    invalidTitle: 'This invitation is no longer valid',
    invalidBody: 'Ask your manager to send you a new one. Links last 7 days, and stop working once the connection is complete.',
    invitedBy: '{manager} invited you to connect your Stripe account',
    forWhat: 'For guest payments at: {what}',
    noAccountNeeded: 'You don\'t need a Guestbook account — this is the only page you\'ll see.',
    termOwnAccount: 'Guest payments go straight to your bank account.',
    termFees: 'Stripe bills you for its own processing fees. Guestbook takes no cut.',
    termVisibility: '{manager} can see whether the account is connected and working — not your documents or your balance.',
    countryLabel: 'Where is your bank account?',
    countryHint: 'This can\'t be changed later, so please check it first.',
    connectCta: 'Connect with Stripe →',
    redirecting: 'Taking you to Stripe…',
    startError: 'The connection couldn\'t be started just now. Please try again shortly.',
    unfinishedTitle: 'Setup isn\'t finished yet',
    unfinishedBody: 'It looks like some steps in Stripe are still outstanding. You can pick up where you left off.',
    continueCta: 'Continue in Stripe →',
    connectedTitle: 'You\'re all set',
    connectedBody: 'Your Stripe account is connected. Payments from guests will arrive in your bank account.',
    connectedDashboard: 'You can close this page — everything else is managed in your own Stripe dashboard.',
  },
  vi: {
    loading: 'Đang tải…',
    invalidTitle: 'Lời mời này không còn hiệu lực',
    invalidBody: 'Hãy đề nghị người quản lý gửi lời mời mới. Liên kết có hiệu lực 7 ngày và sẽ ngừng hoạt động sau khi kết nối hoàn tất.',
    invitedBy: '{manager} mời bạn kết nối tài khoản Stripe của bạn',
    forWhat: 'Cho các khoản thanh toán của khách tại: {what}',
    noAccountNeeded: 'Bạn không cần tài khoản Guestbook — đây là trang duy nhất bạn sẽ thấy.',
    termOwnAccount: 'Tiền khách trả sẽ vào thẳng tài khoản ngân hàng của bạn.',
    termFees: 'Stripe sẽ tính phí xử lý thanh toán của riêng họ. Guestbook không lấy phần trăm nào.',
    termVisibility: '{manager} chỉ thấy tài khoản đã kết nối và hoạt động hay chưa — không thấy giấy tờ hay số dư của bạn.',
    countryLabel: 'Tài khoản ngân hàng của bạn ở nước nào?',
    countryHint: 'Sau này không thể thay đổi, vui lòng kiểm tra kỹ.',
    connectCta: 'Kết nối với Stripe →',
    redirecting: 'Đang chuyển tới Stripe…',
    startError: 'Hiện chưa thể bắt đầu kết nối. Vui lòng thử lại sau ít phút.',
    unfinishedTitle: 'Thiết lập chưa hoàn tất',
    unfinishedBody: 'Có vẻ vẫn còn vài bước chưa xong trên Stripe. Bạn có thể tiếp tục từ chỗ đang dở.',
    continueCta: 'Tiếp tục trên Stripe →',
    connectedTitle: 'Xong rồi',
    connectedBody: 'Tài khoản Stripe của bạn đã được kết nối. Tiền khách trả sẽ về tài khoản ngân hàng của bạn.',
    connectedDashboard: 'Bạn có thể đóng trang này — mọi thứ khác quản lý trong tài khoản Stripe của bạn.',
  },
  uk: {
    loading: 'Завантаження…',
    invalidTitle: 'Це запрошення вже недійсне',
    invalidBody: 'Попросіть менеджера надіслати нове. Посилання діють 7 днів і перестають працювати після завершення підключення.',
    invitedBy: '{manager} запрошує вас підключити ваш рахунок Stripe',
    forWhat: 'Для платежів від гостей: {what}',
    noAccountNeeded: 'Обліковий запис у Guestbook вам не потрібен — це єдина сторінка, яку ви побачите.',
    termOwnAccount: 'Платежі від гостей надходитимуть просто на ваш банківський рахунок.',
    termFees: 'Stripe стягуватиме власні комісії за обробку платежів. Guestbook не бере жодної частки.',
    termVisibility: '{manager} бачитиме лише те, чи рахунок підключений і працює — не ваші документи чи баланс.',
    countryLabel: 'Де ваш банківський рахунок?',
    countryHint: 'Пізніше змінити не можна, тож перевірте, будь ласка.',
    connectCta: 'Підключити Stripe →',
    redirecting: 'Перенаправляємо на Stripe…',
    startError: 'Зараз не вдалося розпочати підключення. Спробуйте, будь ласка, за кілька хвилин.',
    unfinishedTitle: 'Налаштування ще не завершене',
    unfinishedBody: 'Схоже, деякі кроки у Stripe залишилися невиконаними. Ви можете продовжити з того місця, де зупинилися.',
    continueCta: 'Продовжити у Stripe →',
    connectedTitle: 'Готово',
    connectedBody: 'Ваш рахунок Stripe підключено. Платежі від гостей надходитимуть на ваш банківський рахунок.',
    connectedDashboard: 'Цю сторінку можна закрити — усе інше керується у вашому власному кабінеті Stripe.',
  },
};
